import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { Select2ScrollEvent } from 'ng-select2-component';
import { AnimationOptions } from 'ngx-lottie';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import {
  IBackofficeError,
  ICreateBookingFailure,
  IMultipleBookingOutcome,
} from 'src/app/models/booking/multipleBookingOutcome';
import { IReservationRestrictionWarning } from 'src/app/models/booking/restrictionWarning';
import { routingReasonKey } from 'src/app/utils/routingReasons';
import { ReservationFailureReason } from 'src/app/models/enums/reservationFailureReason';
import {
  mergeRestrictionWarnings,
  restrictionWarningsOf,
} from 'src/app/utils/restrictionWarnings';
import {
  NotificationTypes,
  UserStatus,
  UserType,
  SeatProfile,
  UserRole,
} from 'src/app/models/enums';
import { FavouritesService } from 'src/app/services/favourites/favourites.service';
import { images } from 'src/images';
import {
  bookableOriginStops,
  canBookOutsideOutlines,
  getDaysOfWeek,
  hasAvailabilityDetail,
  isDoor2DoorService,
  isTripPlannerService,
  newBookingForm,
  UserBookingData,
} from '../../../forms/bookingForms';
import {
  IAvailability,
  IBookingCreate,
  IBookingScheduler,
  IBookingSeat,
  IFavourites,
  IFindResult,
  IPreferredSeat,
  IReservationAvailability,
  IReservationAvailabilityFailure,
  IServiceInfo,
  IUserRegular,
} from '../../../models';
import {
  BookingSchedulersService,
  BookingsService,
  LiteralService,
  MapUtilsService,
  StorageService,
  TownsService,
  UsersService,
  UtilsService,
} from '../../../services';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { APP } from 'src/app/config/app';

@Component({
  selector: 'app-booking-modal',
  templateUrl: './booking-modal.component.html',
  styleUrl: './booking-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BookingModalComponent implements OnInit, OnChanges, OnDestroy {
  private queryParamsSub?: Subscription;

  @Input() newBooking: IBookingCreate = {} as IBookingCreate;
  public creatingBooking = false;
  @Input() bookingFromFavourite = false;
  @Input() bookingFromUsers = false;
  @Input() serviceInfos: IServiceInfo[] = [];
  @Input() closeExceedingKm? = false;
  @Input() stopToSelect?: any;
  @Output() getServiceInfosEventEmitter = new EventEmitter<any>();
  @Output() selectODEventEmitter = new EventEmitter<any>();
  @Output() goToList = new EventEmitter<any>();
  @Output() showMaxReservationTimeModalEventEmitter = new EventEmitter<any>();
  @Output() showExceedingKmLimitModalEventEmitter = new EventEmitter<any>();
  @Output() showBlockedUserModalEventEmitter = new EventEmitter<any>();
  @Output() selectStop = new EventEmitter<any>();
  @Output() openFavourite = new EventEmitter<number>();
  @Output() changeTown = new EventEmitter<any>();
  @Output() showAlterationModal = new EventEmitter();
  @Output() showChangeSeatsModal = new EventEmitter();
  @Output() showRestrictionWarningsModal = new EventEmitter<IReservationRestrictionWarning[]>();

  public bookingForm: any;
  public availabilities: IFindResult[] = [];

  public restrictionWarnings: IReservationRestrictionWarning[] = [];
  private bookingsAwaitingAcceptance: IBookingCreate[] = [];
  private bookingsCreated = 0;
  private bookingMultiple = false;

  public hasAlterations = false;
  public showPrm: boolean = true;

  public currentStep: number = 1;
  public numberSteps: number = 3;

  public steps = [1];

  private stops: any[] = [];

  public availabilitiesDates: any[] = [];
  public availabilitiesSeparateByDate: any = {};
  public availabilitiesSelected: any = [];
  public availabilityDetail?: IAvailability[];
  public loadingAvailabilities = false;
  public currentPage = 0;

  public currentElement = 1;
  public totalElements = 0;

  public images = images;
  public Object = Object;
  public moment = moment;

  public isBack = false;
  /** While ngOnInit is restoring state from the URL, replace history entries
   * instead of pushing new ones — otherwise a paste/refresh leaves a bogus
   * step-1 entry behind that browser-back lands on. */
  private isInitializing = false;

  public favourite: IFavourites | undefined;
  public dataLoaded = false;
  public favouriteByParams = false;
  public newFavourite: boolean = false;
  public creatingNewFavourite = false;
  public favouriteIsAlreadyRecurring = false;
  public editFavourite = false;

  public stopOriginNotification = false;
  public stopDestinationNotification = false;

  public isSingleReservation: boolean;
  public isD2DBooking: boolean;
  public isD2DFreeStopsAllowed: boolean;
  public isTripPlanner: boolean;

  public possibleSwap = false;
  public swappingStops = false;

  public rebookBooking: IBookingDetails;

  public isEditRecurrent = false;
  public creatingRecurrent = false;
  public editingRecurrent = false;

  public optionsPin: AnimationOptions = {
    path: '/assets/animations/pin.json',
  };

  public optionsBus: AnimationOptions = {
    path: '/assets/animations/bus.json',
  };

  public star: AnimationOptions = {
    path: '/assets/animations/star.json',
  };

  public adminRole: UserRole;

  constructor(
    private usersService: UsersService,
    private townsService: TownsService,
    private bookingsService: BookingsService,
    private bookingSchedulersService: BookingSchedulersService,
    private favouritesService: FavouritesService,
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private mapsUtilsService: MapUtilsService,
    private storageService: StorageService
  ) {}

  async ngOnInit() {
    this.isInitializing = true;
    this.newBooking.recurrentBooking = false;
    this.newBooking.sendNotification = true;

    this.storageService.getRole().then((role) => {
      this.adminRole = role;
    });
    const params = this.activatedRoute.snapshot.queryParamMap;
    const dateTime = params.get('dateTime');
    const urlDate = params.get('date');
    const urlTime = params.get('time');
    const urlTownId = Number(params.get('townId'));
    if (urlTownId) this.newBooking.townId = urlTownId;
    if (urlDate || urlTime) {
      // Explicit ?date= / ?time= from a shared link take precedence.
      if (urlDate) this.newBooking.date = urlDate;
      if (urlTime) this.newBooking.time = urlTime;
    } else if (dateTime) {
      this.newBooking.date = moment(dateTime).format('YYYY-MM-DD');
      this.newBooking.time = moment(dateTime).format('HH:mm');
    } else {
      if (moment(this.newBooking.date).isValid()) {
        this.newBooking.date =
          moment(this.newBooking.date).format('YYYY-MM-DD') ||
          moment().format('YYYY-MM-DD');
      } else {
        const dates = this.newBooking.date.split(',');
        this.newBooking.date = dates.toString();
      }
    }

    // URL-derived step (used when restoring from a deep link / browser back).
    // ngOnInit only runs once per route activation; live URL changes are handled
    // by the queryParamMap subscription set up below.
    const stepParam = Number(params.get('step'));
    if (stepParam >= 1 && stepParam <= this.numberSteps) {
      this.currentStep = stepParam;
      this.steps = Array.from({ length: stepParam }, (_, i) => i + 1);
    }

    const restoringResults = this.currentStep === 3;
    this.loadingAvailabilities = restoringResults;
    try {
      await this.restoreFromUrl(params);
    } catch (error) {
      console.error('Could not restore the booking from the URL', error);
    }
    // getAvailabilities always clears the flag, so still loading means the search never ran
    if (restoringResults && this.loadingAvailabilities) {
      this.loadingAvailabilities = false;
      this.goToStep(this.newBooking.targetUserId ? 2 : 1);
    }

    this.isInitializing = false;

    // React to live URL changes (back/forward, step navigation, etc.).
    this.queryParamsSub?.unsubscribe();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe((p) => {
      const s = Number(p.get('step'));
      const target = s >= 1 && s <= this.numberSteps ? s : 1;
      if (target !== this.currentStep) {
        this.currentStep = target;
        this.steps = Array.from({ length: target }, (_, i) => i + 1);
      }
    });
  }

  ngOnDestroy() {
    this.queryParamsSub?.unsubscribe();
  }

  private async restoreFromUrl(params: ParamMap) {
    if (
      this.bookingFromFavourite &&
      params.get('recurrentId') &&
      params.get('userId') &&
      !this.isBack
    ) {
      this.newBooking.recurrentBooking = true;
      this.isEditRecurrent = true;
      const recurrentId = params.get('recurrentId');
      const userId = params.get('userId');
      const recurrent =
        await this.bookingSchedulersService.getBookingSchedulersById(
          Number(recurrentId),
        );
      const favourite: IFavourites = await this.favouritesService.getFavourite(
        recurrent.favorite.id!,
      );
      this.newBooking.scheduler = recurrent;
      this.newBooking.user = (await this.usersService.getUser(
        UserType.Regular,
        Number(userId),
      )) as IUserRegular;
      this.newBooking.favourite = favourite;
      this.newFavourite = false;
      this.newBooking.scheduler.daysOfWeek = getDaysOfWeek(
        this.literalService,
        this.newBooking,
      );
      await this.setData(
        this.newBooking.user,
        this.newBooking.favourite,
        undefined,
      );
      await this.applyUrlOverrides(params);
    } else if (this.bookingFromUsers && params.get('userId') && !this.isBack) {
      const userId = params.get('userId');
      this.newBooking.user = (await this.usersService.getUser(
        UserType.Regular,
        Number(userId),
      )) as IUserRegular;
      await this.setData(this.newBooking.user);
      await this.applyUrlOverrides(params);
    } else if (
      this.bookingFromFavourite &&
      params.get('userId') &&
      !this.isBack
    ) {
      this.favouriteByParams = true;
      const favouriteId = params.get('favouriteId');
      const userId = params.get('userId');
      this.editFavourite = params.get('editFavourite') === 'true';
      this.favouriteIsAlreadyRecurring =
        history.state.isAlreadyRecurring || false;
      this.newBooking.user = (await this.usersService.getUser(
        UserType.Regular,
        Number(userId),
      )) as IUserRegular;
      if (this.editFavourite) {
        this.newFavourite = true;
        const favourite: IFavourites =
          await this.favouritesService.getFavourite(Number(favouriteId));
        this.favourite = favourite;
        this.newBooking.favouriteName = favourite.name;
        this.newBooking.time =
          (this.favourite.time &&
            moment(this.favourite.time, 'HH:mm').format('HH:mm')) ||
          moment().format('HH:mm');
        this.newBooking.favourite = this.favourite;
      } else {
        if (favouriteId) {
          this.newFavourite = false;
          const favourite: IFavourites =
            await this.favouritesService.getFavourite(Number(favouriteId));
          this.favourite = favourite;
          this.newBooking.favourite = this.favourite;
        } else {
          this.newFavourite = true;
          this.newBooking.favouriteName = '';
          this.favourite = undefined;
          this.newBooking.time =
            (this.newBooking.time &&
              moment(this.newBooking.time, 'HH:mm').format('HH:mm')) ||
            moment().format('HH:mm');
        }
      }
      await this.setData(
        this.newBooking.user,
        this.newBooking.favourite,
        undefined,
      );
      await this.applyUrlOverrides(params);
    } else if (params.get('bookingId') && !this.isBack) {
      const bookingId = Number(params.get('bookingId'));
      const cancelled = params.get('cancelled') === 'true';
      this.rebookBooking = (await this.bookingsService.getBooking(
        bookingId,
        cancelled,
      )) as IBookingDetails;
      this.newBooking.user = (await this.usersService.getUser(
        UserType.Regular,
        Number(this.rebookBooking.userId),
      )) as IUserRegular;
      await this.setData(undefined, undefined, this.rebookBooking);
      this.dataLoaded = true;
      await this.applyUrlOverrides(params);
    } else if (params.get('userId') && !this.isBack) {
      // Fresh entry with userId in URL (e.g., browser back/forward, deep link).
      const userId = params.get('userId');
      this.newBooking.user = (await this.usersService.getUser(
        UserType.Regular,
        Number(userId),
      )) as IUserRegular;
      await this.setData(this.newBooking.user);
      await this.applyUrlOverrides(params);
    } else {
      if (!this.newBooking.destinationStops)
        this.newBooking.destinationStops = [];
      this.newBooking.time =
        (this.newBooking.time &&
          moment(this.newBooking.time, 'HH:mm').format('HH:mm')) ||
        moment().format('HH:mm');
      await this.updateBookingForm(
        new UserBookingData({ showMoreUsers: false }),
      );
    }
  }

  /**
   * Apply form-state from the URL (originStop/destStop/seats/recurrent/…) on
   * top of whatever setData seeded. Used to make a copy-pasted /bookings/new
   * URL faithfully recreate the form.
   */
  private async applyUrlOverrides(params: ParamMap) {
    // Apply scalar flags first. set()/setDestinationStops() below trigger
    // syncFullState, which would otherwise null these out of the URL because
    // they hadn't been copied into newBooking yet — losing them on reload.
    if (params.get('multipleDates') === '1') {
      this.newBooking.multipleDates = true;
    }
    if (params.get('arrival') === '1') {
      this.newBooking.isArrivalTime = true;
    }
    if (params.get('notify') === '0') {
      this.newBooking.sendNotification = false;
    }
    const favouriteName = params.get('favouriteName');
    if (favouriteName) this.newBooking.favouriteName = favouriteName;

    const originStopId = Number(params.get('originStopId'));
    const originLat = Number(params.get('originLat'));
    const originLng = Number(params.get('originLng'));
    const originName = params.get('originName');
    // The URL holds a state that already passed the outline check, and the outlines may not be drawn yet
    if (this.isD2DBooking && originLat && originLng) {
      // Forward the id when present so the handler restores the real-stop identity
      // (not just a free position) on reload.
      await this.set(
        {
          id: originStopId || undefined,
          lat: originLat,
          lng: originLng,
          name: originName,
        },
        'originStopId',
        true,
      );
    } else if (
      !this.isD2DBooking &&
      originStopId &&
      originStopId !== this.newBooking.originStopId
    ) {
      await this.set(originStopId, 'originStopId');
    }

    const destStopId = Number(params.get('destStopId'));
    const destLat = Number(params.get('destLat'));
    const destLng = Number(params.get('destLng'));
    const destName = params.get('destName');
    const currentDestId = this.newBooking.destinationStops?.[0]?.exitStop?.id;
    if (this.isD2DBooking && destLat && destLng) {
      await this.setDestinationStops({
        id: destStopId || undefined,
        lat: destLat,
        lng: destLng,
        name: destName,
      }, true);
    } else if (
      !this.isD2DBooking &&
      destStopId &&
      destStopId !== currentDestId
    ) {
      await this.setDestinationStops(destStopId);
    }

    // Reconcile seat counts. setDestinationStops puts a single seat; the URL
    // may want N+M (regular+prm). We add/remove via the same +/- key path the
    // UI uses so all the side effects fire.
    const wantSeats = Number(params.get('seats')) || 0;
    const wantPrm = Number(params.get('prm')) || 0;
    if (wantSeats || wantPrm) {
      const haveSeats =
        this.newBooking.destinationStops?.filter((s: IBookingSeat) => !s.prm)
          .length || 0;
      const havePrm =
        this.newBooking.destinationStops?.filter((s: IBookingSeat) => s.prm)
          .length || 0;
      if (wantSeats !== haveSeats) {
        await this.setDestinationStops({
          key: 'value',
          value: wantSeats,
          type: 'seats',
        });
      }
      if (wantPrm !== havePrm) {
        await this.setDestinationStops({
          key: 'value',
          value: wantPrm,
          type: 'prm',
        });
      }
    }

    if (params.get('recurrent') === '1') {
      this.newBooking.recurrentBooking = true;
      this.changeRecurrentBooking();
      if (this.newBooking.scheduler) {
        const repeatEvery = params.get('repeatEvery');
        if (repeatEvery)
          this.newBooking.scheduler.repeatEvery = repeatEvery as any;
        const days = (params.get('days') || '').split(',').filter(Boolean);
        if (days.length) this.newBooking.scheduler.daysOfWeek = days as any;
        const startDate = params.get('startDate');
        if (startDate) this.newBooking.scheduler.startDate = startDate;
        const endDate = params.get('endDate');
        if (endDate) this.newBooking.scheduler.endDate = endDate;
        const recurrentName = params.get('recurrentName');
        if (recurrentName) this.newBooking.scheduler.name = recurrentName;
        const recurrentTime = params.get('recurrentTime');
        if (recurrentTime)
          this.newBooking.scheduler.bookingTime = recurrentTime;
      }
    }

    await this.updateBookingForm();

    // If the shared link points at step 3 and we have everything we need to
    // fetch availabilities, do it. Without this, pasting a step-3 URL lands
    // on a blank availabilities pane until the user manually clicks back+next.
    if (Number(params.get('step')) === 3 && this.canFetchAvailabilities()) {
      await this.getAvailabilities();
    } else {
      this.syncFullState();
    }
  }

  /** Can we call getAvailabilities() yet? */
  private canFetchAvailabilities(): boolean {
    const b = this.newBooking;
    if (!b?.targetUserId || !b.townId || !b.date || !b.time) return false;
    const hasOrigin = this.isD2DBooking
      ? !!(b.originStop?.lat && b.originStop?.lng)
      : !!b.originStopId;
    const hasDest = this.isD2DBooking
      ? !!(b.destinationStop?.lat && b.destinationStop?.lng)
      : !!b.destinationStops?.[0]?.exitStop?.id;
    return hasOrigin && hasDest && (b.destinationStops?.length || 0) > 0;
  }

  /** Advance/return to a step, keeping component state and URL in sync. */
  private goToStep(target: number) {
    if (target < 1 || target > this.numberSteps) return;
    this.steps = Array.from({ length: target }, (_, i) => i + 1);
    this.currentStep = target;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { step: target > 1 ? target : null },
      queryParamsHandling: 'merge',
      replaceUrl: this.isInitializing,
    });
  }

  /**
   * Merge form-state into the URL (userId, townId, serviceId, date, time, …).
   * Pass `null` for any key to clear it. The queryParamMap subscription guards
   * against re-applying state when these change, so this is safe to call freely.
   */
  private syncUrlParams(patch: {
    [key: string]: string | number | boolean | null;
  }) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: patch,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Mirror the current newBooking state into the URL so the form is fully
   * recreatable from a shared link. Called after every state mutation; nulls
   * out keys whose value is empty/undefined so they don't linger.
   */
  private syncFullState() {
    const b = this.newBooking || ({} as IBookingCreate);
    const seats =
      b.destinationStops?.filter((s: IBookingSeat) => !s.prm).length || 0;
    const prmSeats =
      b.destinationStops?.filter((s: IBookingSeat) => s.prm).length || 0;
    const patch: { [k: string]: string | number | boolean | null } = {
      userId: b.targetUserId ?? null,
      townId: b.townId ?? null,
      step: this.currentStep > 1 ? this.currentStep : null,
      date: b.date || null,
      time: b.time || null,
      seats: seats || null,
      prm: prmSeats || null,
      multipleDates: b.multipleDates ? '1' : null,
      arrival: b.isArrivalTime ? '1' : null,
      notify: b.sendNotification === false ? '0' : null,
      recurrent: b.recurrentBooking ? '1' : null,
      favouriteName: b.favouriteName || null,
    };
    if (this.isD2DBooking) {
      patch['originLat'] = b.originStop?.lat ?? null;
      patch['originLng'] = b.originStop?.lng ?? null;
      patch['originName'] = b.originStop?.name ?? null;
      patch['destLat'] = b.destinationStop?.lat ?? null;
      patch['destLng'] = b.destinationStop?.lng ?? null;
      patch['destName'] = b.destinationStop?.name ?? null;
      // Preserve id when the user picked a real stop so reloads can restore the
      // full identity (free positions leave the id absent).
      patch['originStopId'] = b.originStopId || b.originStop?.id || null;
      patch['destStopId'] = b.destinationStops?.[0]?.exitStop?.id ?? null;
    } else {
      patch['originStopId'] = b.originStopId || b.originStop?.id || null;
      patch['destStopId'] = b.destinationStops?.[0]?.exitStop?.id ?? null;
      patch['originLat'] = null;
      patch['originLng'] = null;
      patch['originName'] = null;
      patch['destLat'] = null;
      patch['destLng'] = null;
      patch['destName'] = null;
    }
    if (b.recurrentBooking && b.scheduler) {
      patch['repeatEvery'] = (b.scheduler.repeatEvery as any) ?? null;
      const days = (b.scheduler.daysOfWeek || [])
        .map((d: any) => d?.code || d?.value || d)
        .filter(Boolean)
        .join(',');
      patch['days'] = days || null;
      patch['startDate'] = b.scheduler.startDate || null;
      patch['endDate'] = b.scheduler.endDate || null;
      patch['recurrentName'] = b.scheduler.name || null;
      patch['recurrentTime'] = b.scheduler.bookingTime || null;
    } else {
      patch['repeatEvery'] = null;
      patch['days'] = null;
      patch['startDate'] = null;
      patch['endDate'] = null;
      patch['recurrentName'] = null;
      patch['recurrentTime'] = null;
    }
    this.syncUrlParams(patch);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['stopToSelect'] && changes['stopToSelect'].currentValue) {
      if (
        changes['stopToSelect'].currentValue.stop.name ===
          this.newBooking.originStop?.name ||
        changes['stopToSelect'].currentValue.stop.name ===
          this.newBooking.destinationStop?.name
      ) {
        this.onClearElement(
          changes['stopToSelect'].currentValue.isOrigin
            ? 'origin'
            : 'destination',
        );
      } else {
        this.set(
          this.isD2DBooking
            ? changes['stopToSelect'].currentValue.stop
            : changes['stopToSelect'].currentValue.stop.id,
          changes['stopToSelect'].currentValue.isOrigin
            ? 'originStopId'
            : 'destinationsStops',
        );
      }
    }
    if (
      changes['closeExceedingKm'] &&
      changes['closeExceedingKm'].currentValue
    ) {
      window.location.reload();
    }
    if (changes['newBooking'] && changes['newBooking'].currentValue) {
      if (
        !changes['newBooking'].currentValue.targetUserId &&
        changes['newBooking'].previousValue &&
        changes['newBooking'].previousValue.targetUserId
      ) {
        this.bookingFromFavourite = false;
        this.back();
      } else if (this.bookingFromFavourite) {
        if (changes['newBooking'].currentValue.favourite) {
          this.favouriteByParams = false;
          this.newBooking = {} as IBookingCreate;
          this.newBooking.user = (await this.usersService.getUser(
            UserType.Regular,
            changes['newBooking'].currentValue.targetUserId,
          )) as IUserRegular;
          this.favourite = changes['newBooking'].currentValue.favourite;
          this.newBooking.favourite = this.favourite;
          this.setData(
            this.newBooking.user,
            this.newBooking.favourite,
            undefined,
            false,
          );
        } else {
          this.newFavourite = true;
          this.newBooking.favouriteName = '';
          await this.updateBookingForm();
        }
        this.isBack = false;
      } else if (
        changes['newBooking'].currentValue.originStop ||
        changes['newBooking'].currentValue.destinationStop
      ) {
        //this.getPassengers(); Update pax?
        await this.updateBookingForm(); /// TODO remove?
        if (
          !changes['newBooking'].previousValue.originStop &&
          changes['newBooking'].currentValue.originStop
        )
          this.set(
            changes['newBooking'].currentValue.originStop,
            'originStopId',
            this.allowByPassOutlineCheck(),
          );
        if (
          !changes['newBooking'].previousValue.destinationStop &&
          changes['newBooking'].currentValue.destinationStop
        )
          this.set(
            changes['newBooking'].currentValue.destinationStop,
            'destinationsStops',
            this.allowByPassOutlineCheck(),
          );
        if (this.newBooking.originStop && this.newBooking.destinationStop)
          this.possibleSwap = true;
        else this.possibleSwap = false;
        // Seats-modal submit reassigns newBooking from the parent with a new
        // destinationStops array; mirror the new pax/prm counts into the URL.
        const prevLen =
          changes['newBooking'].previousValue?.destinationStops?.length ?? 0;
        const currLen =
          changes['newBooking'].currentValue?.destinationStops?.length ?? 0;
        if (prevLen !== currLen) this.syncFullState();
        this.changeDetectorRef.detectChanges();
      }
    }
    if (changes['serviceInfos'] && changes['serviceInfos'].currentValue) {
      this.hasAlterations = changes['serviceInfos'].currentValue.some(
        (serviceInfo: IServiceInfo) =>
          serviceInfo.alterationMessages.length > 0,
      );
    }
  }

  async getOriginStopData(
    favourite?: IFavourites,
    bookingDetail?: IBookingDetails,
  ) {
    if (bookingDetail)
      return {
        id: bookingDetail.origin.mergedStop
          ? bookingDetail.origin.mergedStop.id
          : bookingDetail.origin.serviceStopId,
        name: bookingDetail.origin.name,
        lat: bookingDetail.origin.latitude,
        lng: bookingDetail.origin.longitude,
      };
    if (favourite)
      return {
        id: favourite.inStop?.id,
        name: favourite.inStop?.name,
        lat: favourite.inStop?.latitude,
        lng: favourite.inStop?.longitude,
      };
    return this.newBooking.originStop;
  }

  async getDestinationStopData(
    favourite?: IFavourites,
    bookingDetail?: IBookingDetails,
  ) {
    if (bookingDetail)
      return {
        id: bookingDetail.destination.mergedStop
          ? bookingDetail.destination.mergedStop.id
          : bookingDetail.destination.serviceStopId,
        name: bookingDetail.destination.name,
        lat: bookingDetail.destination.latitude,
        lng: bookingDetail.destination.longitude,
      };
    if (favourite)
      return {
        id: favourite.outStop?.id,
        name: favourite.outStop?.name,
        lat: favourite.outStop?.latitude,
        lng: favourite.outStop?.longitude,
      };
    return this.newBooking.destinationStop;
  }

  getTownId(
    user?: IUserRegular,
    favourite?: IFavourites,
    bookingDetail?: IBookingDetails,
  ) {
    if (bookingDetail) return bookingDetail.service.town!.id!;
    if (favourite) return favourite.town!.id!;
    // A townId already on newBooking comes from the URL (deep link / reload) and
    // should win over the user's default town.
    if (this.newBooking.townId) return this.newBooking.townId;
    if (user) return user.town!.id!;
    return this.newBooking.townId;
  }

  private getAllServiceStops(): any[] {
    const stops = this.serviceInfos.flatMap((serviceInfo: IServiceInfo) => serviceInfo.stops);
    return Array.from(new Map(stops.map((stop: any) => [stop.id, stop])).values());
  }

  async setData(
    user?: IUserRegular,
    favourite?: IFavourites,
    bookingDetail?: IBookingDetails,
    showBlockedUserModal: boolean = true,
  ) {
    this.dataLoaded = false;
    this.newBooking.targetUserId = bookingDetail
      ? bookingDetail.userId!
      : user!.id!;
    if (
      this.newBooking.user &&
      this.newBooking.user.status?.toUpperCase() === UserStatus.BLOCKED &&
      showBlockedUserModal &&
      !this.newFavourite
    ) {
      const penalty = {
        penaltyEndDate: this.newBooking.user.penaltyEndDate,
        penaltyReason: this.newBooking.user.penaltyReason,
      };
      this.showBlockedUserModal(penalty);
    }
    await this.updateBookingForm(
      new UserBookingData({ showMoreUsers: false }),
      ['user'],
    );
    // Only advance from step 1; deep links land directly on step 2/3 and we
    // mustn't drag the user back to step 2 when restoring step 3.
    if (this.currentStep < 2) this.goToStep(2);
    if (!this.isBack) {
      this.newBooking.townId = this.getTownId(user, favourite, bookingDetail);
      this.serviceInfos = await this.townsService.getServices(this.newBooking.townId);
      this.showPrm = this.serviceInfos.some((serviceInfo: any) => serviceInfo.prmSeatsAvailable);
      this.isD2DBooking = isDoor2DoorService(this.serviceInfos);
      this.isD2DFreeStopsAllowed = this.isD2DBooking;
      this.isTripPlanner = isTripPlannerService(this.serviceInfos);
      this.newBooking.door2DoorOriginFreeStops = this.isD2DFreeStopsAllowed;
      this.newBooking.door2DoorDestinationFreeStops = this.isD2DFreeStopsAllowed;
      await this.updateBookingForm(undefined, !this.isBack && ['town'] || ['']);
      this.getServiceInfosEventEmitter.emit({serviceInfos: this.serviceInfos, townId: this.newBooking.townId});
    }
    this.isD2DBooking = isDoor2DoorService(this.serviceInfos);
    this.isD2DFreeStopsAllowed = this.isD2DBooking;
    this.isTripPlanner = isTripPlannerService(this.serviceInfos);

    if (!this.isD2DBooking) {
      // [DISABLED] singleStopReservation feature
      // if (this.isSingleReservation) {
      //   this.stops = this.newBooking.stops;
      // } else {
      this.stops = this.isTripPlanner
        ? this.getAllServiceStops()
        : await this.townsService.getStopsFrom(
            this.newBooking.townId,
            this.newBooking.originStopId,
          );
      // }
    }
    if (this.isD2DBooking) {
      this.newBooking.door2DoorOriginFreeStops = this.isD2DFreeStopsAllowed;
      this.newBooking.door2DoorDestinationFreeStops = this.isD2DFreeStopsAllowed;
    }
    this.newBooking.originStop = await this.getOriginStopData(
      favourite,
      bookingDetail,
    );
    // D2D services can carry either real predefined stops or arbitrary free
    // points. A rebook or a favourite must mirror whichever the original used:
    //   - real stop → keep the numeric id so step 3 hits the stop endpoint.
    //   - free point → strip the id so step 3 hits the position endpoint with
    //     lat/lng (matches a fresh free-point booking). Favourites persist free
    //     points with a sentinel id (APP.FAVOURITE_FREE_IN_STOP_ID), which must
    //     never reach the availabilities call as a stop id.
    // Detection: the origin is a real stop if its id appears in the loaded
    // service's predefined stops list. Otherwise treat it as a free point.
    if (
      this.isD2DBooking &&
      (bookingDetail || favourite) &&
      this.newBooking.originStop
    ) {
      const originIsRealStop = this.serviceInfos.some((serviceInfo: any) =>
        serviceInfo.stops.some(
          (stop: any) => stop.id === this.newBooking.originStop?.id,
        ),
      );
      if (!originIsRealStop) {
        this.newBooking.originStop = {
          ...this.newBooking.originStop,
          id: undefined,
        };
      } else {
        // Real stop in a D2D area → form should open in stop-list mode, not free-stops.
        this.newBooking.door2DoorOriginFreeStops = false;
        // The stop-list form values for the destination input come from
        // this.stops. setData skips the fetch for D2D, so populate it now so
        // the form can match booking.destinationStop.id back to a list entry.
        this.stops = this.isTripPlanner
          ? this.getAllServiceStops()
          : await this.townsService.getStopsFrom(
              this.newBooking.townId,
              this.newBooking.originStop.id,
            );
      }
    }
    this.newBooking.originStopId = this.newBooking.originStop?.id;
    bookingDetail &&
      (!this.isD2DBooking || this.newBooking.originStopId) &&
      this.setLocation(bookingDetail.origin.serviceStopId!);
    favourite && this.setLocation(favourite!.inStop!.id!);
    await this.updateBookingForm(undefined, ['stop']);
    this.selectODEventEmitter.emit({
      newBooking: this.newBooking,
      type: 'origin',
    });
    this.selectStop.emit({
      stopId: this.newBooking.originStopId,
      townId: this.newBooking.townId,
      destinationsStops: this.stops,
    });

    const serviceInfoData =
      this.serviceInfos.find((serviceInfo: any) =>
        serviceInfo.stops.some(
          (stop: any) => stop.id === this.newBooking.originStop?.id,
        ),
      ) || this.serviceInfos[0];
    this.newBooking.serviceInfo = serviceInfoData;

    if (!this.isBack) {
      this.newBooking.destinationStops = [];
      this.newBooking.destinationStop = await this.getDestinationStopData(
        favourite,
        bookingDetail,
      );
      // Same conditional as originStop above: keep the id only if the booking
      // detail's / favourite's destination corresponds to a real predefined stop
      // in the service. Otherwise treat it as a free point so the call routes to
      // the position endpoint.
      if (
        this.isD2DBooking &&
        (bookingDetail || favourite) &&
        this.newBooking.destinationStop
      ) {
        const destIsRealStop = this.serviceInfos.some((serviceInfo: any) =>
          serviceInfo.stops.some(
            (stop: any) => stop.id === this.newBooking.destinationStop?.id,
          ),
        );
        if (!destIsRealStop) {
          this.newBooking.destinationStop = {
            ...this.newBooking.destinationStop,
            id: undefined,
          };
        } else {
          this.newBooking.door2DoorDestinationFreeStops = false;
        }
      }
      (bookingDetail || favourite) &&
        this.addSeat({
          outStopId: this.newBooking.destinationStop?.id,
          pax: bookingDetail ? bookingDetail.seats : favourite!.passengers,
          prm: bookingDetail
            ? bookingDetail.prmSeats
            : favourite!.prmPassengers,
          seats: bookingDetail
            ? bookingDetail.bookingSeats
            : favourite!.favoriteSeats,
        });
      await this.updateBookingForm();
      this.selectODEventEmitter.emit({
        newBooking: this.newBooking,
        type: 'destination',
      });
    }

    this.newBooking.date =
      moment(this.newBooking.date).format('YYYY-MM-DD') ||
      moment().format('YYYY-MM-DD');
    this.newBooking.time = bookingDetail
      ? moment(
          bookingDetail.originPassingTime ?? bookingDetail.initialOriginTime,
          'HH:mm',
        ).format('HH:mm')
      : (this.newBooking.time &&
          moment(this.newBooking.time, 'HH:mm').format('HH:mm')) ||
        favourite?.time! ||
        moment().format('HH:mm');
    await this.updateBookingForm();
    if (this.favouriteByParams) {
      this.dataLoaded = true;
    }
    this.syncFullState();
  }

  public changeRecurrentBooking() {
    if (this.newBooking.recurrentBooking) {
      this.newBooking.scheduler = {
        repeatEvery: undefined,
        daysOfWeek: [],
        bookingTime: this.newBooking.favourite?.time,
        favorite: { id: this.newBooking.favourite?.id },
        endDate: null,
        startDate: null,
      };
    }
    this.updateBookingForm();
    this.syncFullState();
  }

  public async updateBookingForm(
    userBookingData?: UserBookingData,
    additionalFields = [''],
  ) {
    // [DISABLED] singleStopReservation feature
    // [DISABLED] singleStopReservation feature
    // this.isSingleReservation = this.serviceInfos?.some((serviceInfo: IServiceInfo) => serviceInfo.singleStopReservations);
    const favouriteOrRebookSingle = false; // [DISABLED] was: this.isSingleReservation && (this.bookingFromFavourite || this.rebookBooking !== undefined);
    this.bookingForm = await newBookingForm(
      this.newBooking,
      this.usersService,
      this.literalService,
      userBookingData,
      this.serviceInfos,
      this.stops,
      additionalFields,
      favouriteOrRebookSingle,
      this.newFavourite,
      this.adminRole,
    );
  }

  private resetMultipleDatesForTripPlanner() {
    if (!this.isTripPlanner || !this.newBooking.multipleDates) return;
    this.newBooking.multipleDates = false;
    this.newBooking.date = moment().format('YYYY-MM-DD');
  }

  private getStopsReachableFromOrigin() {
    if (this.isTripPlanner) return Promise.resolve(this.getAllServiceStops());
    const originStop = this.newBooking.originStop;
    const originStopId = typeof originStop.id === 'number' ? originStop.id : undefined;
    return this.townsService.getStopsFrom(this.newBooking.townId, originStopId, undefined, undefined, undefined, originStop.lat, originStop.lng);
  }

  private allowByPassOutlineCheck() {
    return canBookOutsideOutlines(this.adminRole);
  }

  private getD2DOutlineError(position: google.maps.LatLngLiteral, oppositePosition: google.maps.LatLngLiteral, bypassOutlineCheck: boolean) {
    if (bypassOutlineCheck || !this.isD2DBooking || this.allowByPassOutlineCheck()) return null;
    if (!this.mapsUtilsService.isPointInsideOutlines(position.lat, position.lng)) {
      return 'bookings.actions.createBooking.outsideOutline';
    }
    if (!this.mapsUtilsService.checkIfStopsAreSameOutline(position, oppositePosition)) {
      return 'bookings.actions.createBooking.differentOutlines';
    }
    return null;
  }
  
  async set(data: any, valueToChange: string, bypassOutlineCheck = false) {
    switch (valueToChange) {
      case 'targetUserId': {
        if (this.newBooking.targetUserId !== data.id) {
          this.newBooking.targetUserId = data.id;
          this.newBooking.user = (await this.usersService.getUser(
            UserType.Regular,
            data.id,
          )) as IUserRegular;

          if (
            this.newBooking.user?.status?.toUpperCase() === UserStatus.BLOCKED
          ) {
            const { penaltyEndDate, penaltyReason } = this.newBooking.user;
            this.showBlockedUserModal({ penaltyEndDate, penaltyReason });
          }
          this.newBooking.townId = data.town.id;
          this.serviceInfos = await this.townsService.getServices(
            this.newBooking.townId,
          );
          this.showPrm = this.serviceInfos.some(
            (serviceInfo) => serviceInfo.prmSeatsAvailable,
          );
          this.getServiceInfosEventEmitter.emit({
            serviceInfos: this.serviceInfos,
            townId: this.newBooking.townId,
            user: this.newBooking.user,
          });
          this.changeTown.emit(data.town.id);
          this.isD2DBooking = isDoor2DoorService(this.serviceInfos);
          this.isD2DFreeStopsAllowed = this.isD2DBooking;
          this.isTripPlanner = isTripPlannerService(this.serviceInfos);
          this.newBooking.door2DoorOriginFreeStops = this.isD2DFreeStopsAllowed;
          this.newBooking.door2DoorDestinationFreeStops = this.isD2DFreeStopsAllowed;
          this.stops = this.isTripPlanner ? this.getAllServiceStops() : [];
          this.resetMultipleDatesForTripPlanner();
          await this.updateBookingForm(undefined, ['user']);

          // [DISABLED] singleStopReservation feature
          // [DISABLED] singleStopReservation feature
          // this.isSingleReservation = this.serviceInfos?.some((serviceInfo: IServiceInfo) => serviceInfo.singleStopReservations);
          // if (this.isSingleReservation) {
          //   this.stops = this.newBooking.stops;
          // }

          if (!this.steps.includes(2)) this.goToStep(2);
          if (this.isBack) this.isBack = false;
        }
        break;
      }
      case 'town': {
        if (this.newBooking.townId === data) break;
        this.newBooking.townId = data;
        this.onEditCustomStop('origin');
        this.onEditCustomStop('destination');
        this.serviceInfos = await this.townsService.getServices(this.newBooking.townId);
        this.showPrm = this.serviceInfos.some((serviceInfo) => serviceInfo.prmSeatsAvailable);
        // [DISABLED] singleStopReservation feature
        // this.isSingleReservation = this.serviceInfos?.some((serviceInfo: IServiceInfo) => serviceInfo.singleStopReservations);
        this.isD2DBooking = isDoor2DoorService(this.serviceInfos);
        this.isD2DFreeStopsAllowed = this.isD2DBooking;
        this.isTripPlanner = isTripPlannerService(this.serviceInfos);
        this.newBooking.door2DoorOriginFreeStops = this.isD2DFreeStopsAllowed;
        this.newBooking.door2DoorDestinationFreeStops = this.isD2DFreeStopsAllowed;
        this.stops = this.getAllServiceStops();
        this.resetMultipleDatesForTripPlanner();

        await this.updateBookingForm(undefined, ['town']);

        this.getServiceInfosEventEmitter.emit({
          serviceInfos: this.serviceInfos,
          townId: this.newBooking.townId,
        });
        this.changeTown.emit(data);

        this.isBack = false;
        this.dataLoaded = true;
        this.possibleSwap = false;

        // [DISABLED] singleStopReservation feature
        // if (this.isSingleReservation) {
        //   this.stops = this.newBooking.stops;
        // }
        if (this.bookingFromFavourite) this.bookingFromFavourite = false;

        break;
      }
      case 'originStopId': {
        if (
          this.isD2DBooking ||
          (this.isTripPlanner && this.newBooking.door2DoorOriginFreeStops)
        ) {
          if (data.id && this.newBooking.door2DoorOriginFreeStops)
            this.newBooking.door2DoorOriginFreeStops = false;
          if (!this.newBooking.door2DoorOriginFreeStops)
            this.stops = this.newBooking.stops;
          let stop: any = this.stops.find(
            (stop: any) =>
              stop.id === (data.id ?? data) || stop.name === (data.name ?? data),
          );
          if (stop) stop = stop.value || stop;
          else stop = data;
          const lat =
            stop.latitude ||
            stop.lat ||
            stop.location?.geometry?.coordinates[1] ||
            stop.location.lat;
          const lng =
            stop.longitude ||
            stop.lng ||
            stop.location?.geometry?.coordinates[0] ||
            stop.location.lng;
          // If the new origin sits in a different outline than the currently
          // selected destination, clear the destination so the user can pick a
          // new one in the new outline. Previously this case silently kept the
          // existing destination and rejected the origin update — disastrous on
          // rebook, where both endpoints were pre-filled from the original
          // booking: subsequent destination picks also failed the outline check
          // against the still-original origin, both updates were rejected, and
          // step 3 hit the stop endpoint with the original ids.
          const outlineError = this.getD2DOutlineError(
            { lat, lng },
            this.newBooking.destinationStop,
            bypassOutlineCheck,
          );
          if (!outlineError) {
            this.newBooking.originStop = {
              lat,
              lng,
              name: stop.name,
              id: stop?.id,
            };
            // Mirror id to originStopId so URL sync and the stop-based availabilities
            // call treat a real-stop D2D origin the same as a non-D2D one.
            this.newBooking.originStopId = stop?.id;
            // Fire the from-list call whenever we have an origin position. Real-stop
            // origins also pass their id so non-D2D services can use getStopsAfter,
            // while D2D services always rely on lat/lng for the outline check.
            if (
              this.newBooking.originStop?.lat &&
              this.newBooking.originStop?.lng
            ) {
              this.stops = await this.getStopsReachableFromOrigin();
              // Only clear destination when it was a real stop that's no longer reachable;
              // a free-position destination has no id and would otherwise be wiped wrongly.
              if (
                this.newBooking.destinationStop?.id &&
                !this.stops.some(
                  (stop: any) => stop.id === this.newBooking.destinationStop.id,
                )
              ) {
                this.newBooking.destinationStops = [];
                this.newBooking.destinationStop = null;
                this.selectODEventEmitter.emit({
                  newBooking: this.newBooking,
                  type: 'destination',
                  isSingleReservation: false,
                });
              }
            }

            this.selectODEventEmitter.emit({
              newBooking: this.newBooking,
              type: 'origin',
              isSingleReservation: false,
            });
            this.selectStop.emit({
              destinationsStops: this.stops,
              showMarkers: true,
            });
            await this.updateBookingForm(
              undefined,
              this.newBooking.door2DoorOriginFreeStops
                ? ['originStopId']
                : ['stop'],
            );
          } else {
            this.utilsService.showNotification(
              images.notifications.info,
              outlineError,
              NotificationTypes.INFO,
            );
            this.newBooking.originStop = null;
            if (this.newBooking.favourite)
              this.newBooking.favourite.inStop = undefined;
            this.selectODEventEmitter.emit({
              newBooking: this.newBooking,
              type: 'origin',
              isSingleReservation: false,
            });
            await this.updateBookingForm(
              undefined,
              this.newBooking.door2DoorOriginFreeStops
                ? ['originStopId']
                : ['stop'],
            );
          }
        } else {
          this.newBooking.originStopId = data.id || data;

          const serviceInfoData =
            this.serviceInfos.find((serviceInfo) =>
              serviceInfo.stops.some((stop) => stop.id === data),
            ) || this.serviceInfos[0];
          this.newBooking.serviceInfo = serviceInfoData;
          this.setLocation(data);

          // [DISABLED] singleStopReservation feature - removed if (!this.isSingleReservation) guard
          this.stops = this.isTripPlanner
            ? this.getAllServiceStops()
            : await this.townsService.getStopsFrom(
                this.newBooking.townId,
                this.newBooking.originStopId,
              );
          if (
            this.newBooking.destinationStops.length > 0 &&
            !this.stops.some(
              (stop: any) =>
                stop.id === this.newBooking.destinationStops[0].exitStop.id,
            )
          ) {
            this.newBooking.destinationStops = [];
            this.newBooking.destinationStop = null;
            this.selectODEventEmitter.emit({
              newBooking: this.newBooking,
              type: 'destination',
              isSingleReservation: false,
            });
          }

          await this.updateBookingForm(undefined, ['stop']);
          // [DISABLED] singleStopReservation feature
          // let singleStopReservationType = this.newBooking.originStopId ? SingleStopReservationType.ORIGIN : SingleStopReservationType.DESTINATION;
          // if (this.isSingleReservation) {
          //   singleStopReservationType === SingleStopReservationType.ORIGIN && this.selectODEventEmitter.emit({ newBooking: this.newBooking, type: 'origin', isSingleReservation: this.isSingleReservation });
          // } else {
          this.selectODEventEmitter.emit({
            newBooking: this.newBooking,
            type: 'origin',
            isSingleReservation: false,
          });
          // }
          this.selectStop.emit({
            destinationsStops: this.stops,
            showMarkers: true,
          });

          // [DISABLED] singleStopReservation feature
          // if (this.isSingleReservation) {
          //   this.setDestinationStops(undefined);
          // }
        }
        break;
      }
      case 'destinationsStops': {
        this.setDestinationStops(data, bypassOutlineCheck);
        break;
      }
      case 'date': {
        if (data) {
          this.newBooking.date = data
            ? data
                .split(',')
                .map((date: string) =>
                  moment(date.trim(), 'DD/MM/YYYY').format('YYYY-MM-DD'),
                )
                .join(',')
            : '';
        } else {
          this.newBooking.date = '';
        }

        await this.updateBookingForm();
        break;
      }
      case 'time': {
        this.newBooking.time = data;
        await this.updateBookingForm();
        break;
      }
      case 'favouriteName': {
        this.newBooking.favouriteName = data;
        break;
      }
      case 'scheduler.name': {
        this.newBooking.scheduler.name = data;
        break;
      }
      case 'scheduler.endDate': {
        this.newBooking.scheduler.endDate = data;
        break;
      }
      case 'scheduler.startDate': {
        this.newBooking.scheduler.startDate = data;
        await this.updateBookingForm();
        break;
      }
      case 'scheduler.repeatEvery': {
        this.newBooking.scheduler.repeatEvery = data;
        break;
      }
      case 'scheduler.daysOfWeek': {
        this.newBooking.scheduler.daysOfWeek = data;
        break;
      }
      case 'scheduler.bookingTime': {
        this.newBooking.scheduler.bookingTime = data;
        break;
      }
      case 'notify': {
        this.newBooking.sendNotification = data;
        break;
      }
      case 'isArrivalTime': {
        this.newBooking.isArrivalTime = data;
        break;
      }
      default:
        break;
    }
    this.currentStep = this.steps.at(-1)!;
    this.syncFullState();
  }

  addSeat(data: any) {
    let destination: any = 0;
    destination = this.newBooking.destinationStop;
    let bookingSeat: IBookingSeat;
    if (data.seats.length > 0) {
      for (let index = 0; index < data.seats.length; index++) {
        let seat: IBookingSeat = data.seats[index];
        bookingSeat = {
          exitStop: { id: destination.id, name: destination.name },
          prm: seat.prm,
          name: seat.name,
          profile: seat.profile,
          need: seat.need,
          needRequested: seat.needRequested,
          noShow: false,
        };
        this.newBooking.destinationStops.push(bookingSeat);
      }
    } else {
      for (let index = 0; index < data.pax; index++) {
        bookingSeat = {
          exitStop: { id: destination.id, name: destination.name },
          prm: false,
          profile: SeatProfile.Regular,
          needRequested: false,
          noShow: false,
        };
        this.newBooking.destinationStops.push(bookingSeat);
      }
      for (let index = 0; index < data.prm; index++) {
        bookingSeat = {
          exitStop: { id: destination.id, name: destination.name },
          prm: true,
          profile: SeatProfile.Regular,
          needRequested: false,
          noShow: false,
        };
        this.newBooking.destinationStops.push(bookingSeat);
      }
    }
    this.setLocation(this.newBooking.destinationStops[0].exitStop.id!, false);
    if (this.newBooking.originStop && this.newBooking.destinationStop)
      this.possibleSwap = true;
    this.syncFullState();
  }

  async setDestinationStops(data: any, bypassOutlineCheck = false) {
    let destination: any;
    let bookingSeat: IBookingSeat | null = null;
    // [DISABLED] singleStopReservation feature
    // let singleStopReservationType = this.newBooking.originStopId ? SingleStopReservationType.ORIGIN : SingleStopReservationType.DESTINATION;
    if (this.isD2DBooking) {
      if (data.id && this.newBooking.door2DoorDestinationFreeStops)
        this.newBooking.door2DoorDestinationFreeStops = false;
      let stop: any = this.stops.find((stop: any) =>
        this.newBooking.door2DoorDestinationFreeStops
          ? stop.name === data
          : stop.id === data.id ||
            stop.name === data.name ||
            stop.name === data,
      );
      if (stop) stop = stop.value || stop;
      else if (!data.key) stop = data;
      if (stop) {
        const lat =
          stop.latitude ||
          stop.lat ||
          stop.location?.geometry?.coordinates[1] ||
          stop.location.lat;
        const lng =
          stop.longitude ||
          stop.lng ||
          stop.location?.geometry?.coordinates[0] ||
          stop.location.lng;
        const outlineError = this.getD2DOutlineError(
          { lat, lng },
          this.newBooking.originStop,
          bypassOutlineCheck,
        );
        if (!outlineError) {
          this.newBooking.destinationStop = {
            lat,
            lng,
            name: stop.name,
            id: stop?.id,
          };
          bookingSeat = {
            exitStop: {
              id: this.newBooking.destinationStop.id || 0,
              name: this.newBooking.destinationStop.name || data,
            },
            prm: data && data.type === 'prm',
            profile: SeatProfile.Regular,
            needRequested: false,
            noShow: false,
          };
          this.selectStop.emit({
            destinationsStops: this.stops,
            showMarkers: true,
          });
        } else {
          this.utilsService.showNotification(
            images.notifications.info,
            outlineError,
            NotificationTypes.INFO,
          );
          this.newBooking.destinationStop = null;
          this.newBooking.destinationStops = [];
          if (this.newBooking.favourite)
            this.newBooking.favourite.outStop = undefined;
        }
      } else {
        bookingSeat = {
          exitStop: {
            id: this.newBooking.destinationStop.id,
            name: this.newBooking.destinationStop.name,
          },
          prm: data && data.type === 'prm',
          profile: SeatProfile.Regular,
          needRequested: false,
          noShow: false,
        };
      }
    } else {
      destination =
        typeof data === 'object'
          ? { ...this.newBooking.destinationStops }
          : this.stops.find((d: any) => d.id === data);
      // [DISABLED] singleStopReservation feature - was: if (singleStopReservationType === SingleStopReservationType.ORIGIN && !destination)
      if (false && !destination) {
        bookingSeat = {
          exitStop: { id: 0, name: '' },
          prm: data && data.type === 'prm',
          profile: SeatProfile.Regular,
          needRequested: false,
          noShow: false,
        };
      } else {
        bookingSeat = {
          exitStop: {
            id: destination[0]?.exitStop?.id || destination.id,
            name: destination[0]?.exitStop?.name || destination.name,
          },
          prm:
            this.newBooking.destinationStops.length === 0
              ? this.showPrm
                ? this.newBooking.user.prm
                : false
              : data.type === 'prm',
          profile: SeatProfile.Regular,
          needRequested: false,
          noShow: false,
        };
        this.newBooking.serviceInfo =
          this.serviceInfos.find((serviceInfo) =>
            serviceInfo.stops.some(
              (stop) =>
                stop.id === destination[0]?.exitStop?.id || destination.id,
            ),
          ) || this.serviceInfos[0];
      }
    }

    if (bookingSeat) {
      if (typeof data === 'object' && data.key) {
        let index = -1;
        const existingSeat = this.newBooking.destinationStops.find(
          (d: any, i: number) => {
            index = i;
            return d.prm === bookingSeat.prm;
          },
        );
        if (existingSeat && data.key === 'minus') {
          this.newBooking.destinationStops.splice(index, 1);
          if (this.newBooking.destinationStops.length === 0)
            this.newBooking.destinationStop = null;
        } else if (data.key === 'plus')
          this.newBooking.destinationStops.push(bookingSeat);
        else if (data.key === 'value' && data.value !== '') {
          this.newBooking.destinationStops =
            this.newBooking.destinationStops.filter(
              (d: any) => d.prm !== bookingSeat.prm,
            );
          Array.from({ length: data.value }).forEach(() =>
            this.newBooking.destinationStops.push(bookingSeat),
          );
        }
        destination &&
          destination.length &&
          this.setLocation(destination[0].id, false);
      } else {
        this.newBooking.destinationStops =
          this.newBooking.destinationStops.length > 1
            ? this.newBooking.destinationStops.map((d: IBookingSeat) => ({
                ...bookingSeat,
                prm: d.prm,
              }))
            : [bookingSeat];
        destination && this.setLocation(destination.id, false);
      }
    }

    this.newBooking.destinationStops = [...this.newBooking.destinationStops];
    if (this.isD2DBooking) {
      this.newBooking.door2DoorDestinationFreeStops ? (this.stops = []) : null;
      await this.updateBookingForm(
        undefined,
        this.newBooking.door2DoorDestinationFreeStops
          ? ['destinationsStops']
          : ['destination'],
      );
    } else {
      await this.updateBookingForm(undefined, ['destination']);
    }
    // [DISABLED] singleStopReservation feature
    // if (this.isSingleReservation) {
    //   singleStopReservationType === SingleStopReservationType.DESTINATION && this.selectODEventEmitter.emit({ newBooking: this.newBooking, type: 'destination', isSingleReservation: this.isSingleReservation });
    // } else {
    this.selectODEventEmitter.emit({
      newBooking: this.newBooking,
      type: 'destination',
      isSingleReservation: false,
    });
    // }
    if (this.newBooking.originStop && this.newBooking.destinationStop)
      this.possibleSwap = true;
    else this.possibleSwap = false;
    this.syncFullState();
  }

  setLocation(id: number, origin = true) {
    this.serviceInfos.map((serviceInfo: IServiceInfo) => {
      serviceInfo.stops.find((stop: any) => {
        if (stop.id === id) {
          origin
            ? this.serviceInfos.map((serviceInfo: IServiceInfo) => {
                serviceInfo.stops.find((stop: any) => {
                  if (stop.id === id) {
                    this.newBooking.originStop = {
                      lat: stop.location.geometry.coordinates[1],
                      lng: stop.location.geometry.coordinates[0],
                      name: stop.name,
                      id: stop.id,
                    };
                  }
                });
              })
            : this.serviceInfos.map((serviceInfo: IServiceInfo) => {
                serviceInfo.stops.find((stop: any) => {
                  if (stop.id === id) {
                    this.newBooking.destinationStop = {
                      lat: stop.location.geometry.coordinates[1],
                      lng: stop.location.geometry.coordinates[0],
                      name: stop.name,
                      id: stop.id,
                    };
                  }
                });
              });
        }
      });
    });
  }

  async scroll(event: Select2ScrollEvent) {
    if (event.way === 'down') {
      if (event.search !== '') {
        await this.updateBookingForm(
          new UserBookingData({
            showMoreUsers: true,
            search: true,
            value: event.search,
          }),
        );
      } else {
        await this.updateBookingForm(
          new UserBookingData({ showMoreUsers: true }),
        );
      }
    }
  }

  async search(event: any, valueToChange: string) {
    const value = event.search;
    if (valueToChange === 'targetUserId') {
      const dropDown = document.getElementsByClassName(
        'select2-results__options',
      )[0];
      if (value !== '') {
        await this.updateBookingForm(
          new UserBookingData({
            showMoreUsers: false,
            search: true,
            value: value,
          }),
        );
        dropDown.scroll({
          top: 0,
        });
      } else {
        await this.updateBookingForm(
          new UserBookingData({ showMoreUsers: false }),
        );
      }
    } else if (
      valueToChange === 'originStopId' ||
      valueToChange === 'destinationsStops'
    ) {
      if (this.isD2DBooking) {
        this.stops = [];
        if (value !== '') {
          const exactStops = await this.searchByStreetName(value);
          if (exactStops.length > 0) {
            const stop = exactStops[0];
            const nearestRoad: any = await this.mapsUtilsService.getNearestRoad(
              stop.location.lat,
              stop.location.lng,
            );
            if (!this.stops.some((stop) => stop.id === nearestRoad.id)) {
              this.stops.push(nearestRoad);
            }
          }
        }
        this.updateBookingForm(undefined, [valueToChange]);
      }
    }
  }

  async searchByStreetName(nameToSearch: string) {
    const town: any = await this.townsService.getTown(this.newBooking.townId);
    return this.mapsUtilsService.getPlaces(
      nameToSearch,
      town.latitude,
      town.longitude,
    );
  }

  change(data: any, valueToChange: string) {
    if (valueToChange === 'scheduler.daysOfWeek') {
      this.updateBookingForm();
      return;
    } else if (valueToChange === 'destinationsStops') {
      this.set(data, 'destinationsStops');
    } else if (valueToChange === 'notify') {
      this.set(data.target.checked, valueToChange);
    } else {
      this.set(data.target.value, valueToChange);
    }
  }

  async getAvailabilities() {
    window.scrollTo(0, 0);
    this.loadingAvailabilities = true;
    this.availabilityDetail = undefined;
    this.goToStep(3);
    const dates = this.newBooking.date.split(',').filter((date: string) => {
      return date !== '';
    });
    const dateTimes = dates.map((date: any) => {
      return moment(date + ' ' + this.newBooking.time).format(
        'YYYY-MM-DD[T]HH:mm',
      );
    });

    const seats = this.newBooking.destinationStops.filter(
      (destinationStop: IBookingSeat) => !destinationStop.prm,
    ).length;
    const prmSeats = this.newBooking.destinationStops.filter(
      (destinationStop: IBookingSeat) => destinationStop.prm,
    ).length;
    const originStopId =
      this.newBooking.originStop?.id || this.newBooking.originStopId;
    const destStopId = this.newBooking.destinationStops?.[0]?.exitStop?.id;
    // Only a free point needs the position endpoint; two real stops go by id whatever the service
    if (!this.hasRealStopId(originStopId) || !this.hasRealStopId(destStopId)) {
      await this.townsService
        .getAvailabilitiesPositions(
          this.newBooking.townId,
          this.newBooking.targetUserId,
          this.hasRealStopId(originStopId) ? originStopId : undefined,
          this.newBooking.originStop.lat,
          this.newBooking.originStop.lng,
          this.newBooking.originStop.name,
          this.hasRealStopId(destStopId) ? destStopId : undefined,
          this.newBooking.destinationStop.lat,
          this.newBooking.destinationStop.lng,
          this.newBooking.destinationStop.name,
          seats,
          prmSeats,
          dateTimes,
          !!this.newBooking.isArrivalTime,
          undefined, // reservationId?: number
        )
        .then(
          (resp: any) => {
            this.availabilities = this.utilsService.convertDateTimeArrays(resp);
          },
          (error: any) => {
            this.back();
          },
        );
    } else {
      await this.townsService
        .getAvailabilities(
          this.newBooking.townId,
          this.newBooking.targetUserId,
          originStopId!,
          destStopId!,
          seats,
          prmSeats,
          dateTimes,
          !!this.newBooking.isArrivalTime,
          undefined, // reservationId?: number
          false, // [DISABLED] singleStopReservation feature - was: this.isSingleReservation
        )
        .then(
          (resp: any) => {
            this.availabilities = this.utilsService.convertDateTimeArrays(resp);
          },
          (error: any) => {
            this.back();
          },
        );
    }

    dates.forEach((date, index) => {
      this.availabilitiesSeparateByDate[date] = this.normalizeAvailabilityGroup(
        this.availabilities[index],
      );
    });
    this.availabilitiesDates = dates;
    this.availabilitiesDates.sort();
    this.totalElements = Object.keys(this.availabilitiesSeparateByDate).length;
    this.loadingAvailabilities = false;
  }

  // A Google Place id is a string and 0 is what a cleared form leaves behind: neither is a stop
  hasRealStopId(stopId: unknown): stopId is number {
    return typeof stopId === 'number' && stopId !== 0;
  }

  // success arrives as an array of trips, each being an array of legs (a transfer has 2+); keep
  // that grouping so a trip renders/books as one unit instead of one card per leg.
  private normalizeAvailabilityGroup(group: any) {
    if (!group) return group;
    return {
      ...group,
      success: (group.success || []).map((trip: any) => Array.isArray(trip) ? trip : [trip]),
      failure: (group.failure || []).map((trip: any) => Array.isArray(trip) ? trip : [trip]),
    };
  }

  async createBookings(multiple: boolean) {
    this.creatingBooking = true;
    this.bookingMultiple = multiple;
    this.bookingsCreated = 0;
    const tripsToBook = this.tripsToBook(multiple);
    const bookings = this.makeBookingArray(tripsToBook);
    const knownWarnings = this.restrictionWarningsOfTrips(tripsToBook);
    if (knownWarnings.length > 0) {
      this.askBeforeBooking(bookings, knownWarnings);
      return;
    }
    await this.submitBookings(bookings);
  }

  tripsToBook(multiple: boolean) {
    return multiple
      ? this.availabilitiesDates
          .map((date) => this.availabilitiesSeparateByDate[date].success[0])
          .filter((trip) => trip !== undefined)
      : this.availabilitiesSelected;
  }

  async bookOverKmLimit() {
    await this.createBookings(this.bookingMultiple);
  }

  // The search already said what these options step over, so the user is asked before any request
  restrictionWarningsOfTrips(trips: any[]): IReservationRestrictionWarning[] {
    return mergeRestrictionWarnings(
      (trips ?? [])
        .flatMap((trip: any) => (Array.isArray(trip) ? trip : [trip]))
        .flatMap((leg: any) => leg?.restrictionWarnings ?? []),
    );
  }

  private askBeforeBooking(bookings: IBookingCreate[], warnings: IReservationRestrictionWarning[]) {
    this.bookingsAwaitingAcceptance = bookings;
    this.restrictionWarnings = warnings;
    this.creatingBooking = false;
    this.showRestrictionWarningsModal.emit(warnings);
  }

  private async submitBookings(bookings: IBookingCreate[]) {
    try {
      const outcome = await this.bookingsService.multipleBooking(
        bookings,
        this.isTripPlanner,
      );
      this.applyBookingOutcome(bookings, outcome);
    } catch (error: any) {
      this.failBookings(error);
    }
  }

  private applyBookingOutcome(
    submitted: IBookingCreate[],
    outcome: IMultipleBookingOutcome,
  ) {
    const failures = outcome.failures ?? [];
    this.bookingsCreated += outcome.succeeded ?? 0;
    const refused = failures.filter(
      (failure) => restrictionWarningsOf(failure.error).length > 0,
    );
    const failed = failures.filter(
      (failure) => restrictionWarningsOf(failure.error).length === 0,
    );
    if (failed.length > 0) this.notifyBookingFailures(submitted, failed);
    if (refused.length === 0) {
      this.creatingBooking = false;
      if (this.bookingsCreated > 0) this.goToList.emit(this.bookingMultiple);
      else this.back();
      return;
    }
    this.askBeforeBooking(
      this.bookingsToRetry(submitted, refused),
      mergeRestrictionWarnings(refused.flatMap((failure) => restrictionWarningsOf(failure.error))),
    );
  }

  // A trip is booked as one unit, so when it asks the whole batch goes again
  private bookingsToRetry(
    submitted: IBookingCreate[],
    refused: ICreateBookingFailure[],
  ): IBookingCreate[] {
    if (refused.some((failure) => failure.index === null || failure.index === undefined)) {
      return submitted;
    }
    return refused
      .map((failure) => submitted[failure.index!])
      .filter((booking) => !!booking);
  }

  async acceptRestrictions(accepted: ReservationFailureReason[]) {
    const bookings = this.bookingsAwaitingAcceptance.map((booking) => ({
      ...booking,
      acceptedRestrictions: [
        ...new Set([...(booking.acceptedRestrictions ?? []), ...accepted]),
      ],
    }));
    this.bookingsAwaitingAcceptance = [];
    this.restrictionWarnings = [];
    this.creatingBooking = true;
    await this.submitBookings(bookings);
  }

  // Declining leaves the options on screen to pick another one, unless part of the batch already entered
  declineRestrictions() {
    this.bookingsAwaitingAcceptance = [];
    this.restrictionWarnings = [];
    this.creatingBooking = false;
    if (this.bookingsCreated > 0) this.goToList.emit(this.bookingMultiple);
  }

  // Each failed booking is named by its date and time and the reason, so the toast says what to fix
  private notifyBookingFailures(submitted: IBookingCreate[], failed: ICreateBookingFailure[]) {
    const outcomeKey = this.bookingsCreated > 0 ? 'someFailed' : 'noneCreated';
    const title = this.literalService.get(`bookings.actions.createBooking.${outcomeKey}.title`, true);
    const lines = failed.map((failure) => this.bookingFailureLine(submitted, failure));
    this.utilsService.showNotification(
      images.notifications.warning,
      '',
      NotificationTypes.DANGER,
      title,
      lines.join(' · '),
      undefined,
      false,
    );
  }

  private bookingFailureLine(submitted: IBookingCreate[], failure: ICreateBookingFailure): string {
    const reason = this.bookingFailureReason(failure.error);
    const booking = failure.index === null || failure.index === undefined ? undefined : submitted[failure.index];
    const departure = booking ? this.bookedLegDeparture(booking) : undefined;
    return departure ? `${departure}: ${reason}` : reason;
  }

  private bookedLegDeparture(booking: IBookingCreate): string | undefined {
    const leg = Object.values(this.availabilitiesSeparateByDate ?? {})
      .flatMap((group: any) => group.success ?? [])
      .flat()
      .find((candidate: any) => candidate.serviceAvailabilityResponseId === booking.serviceAvailabilityResponseId);
    const dateTime = leg?.inStop?.dateTime;
    return dateTime ? moment(dateTime).format('DD/MM HH:mm') : undefined;
  }

  // The routing reason of an unfeasible booking has the same texts the availability cards use
  bookingFailureReason(error?: IBackofficeError): string {
    if (!error?.errorCode) return this.literalService.get('bookings.Impossible');
    if (error.errorCode === ErrorsCode.UNFEASIBLE_BOOKING) {
      return this.literalService.get(`bookings.${routingReasonKey(error.entityInfo)}`);
    }
    const specificKey = `${error.errorCode}_ERROR.bookings.title`;
    const specific = this.literalService.get(specificKey);
    return specific !== specificKey ? specific : this.literalService.get(`${error.errorCode}_ERROR.title`);
  }

  // Losing the socket does not undo the bookings, so the refreshed list is the only honest answer
  private failBookings(error: any) {
    this.creatingBooking = false;
    const errorCode = error?.error_code ?? error?.errorCode;
    if (!errorCode) {
      this.utilsService.showNotification(
        images.notifications.warning,
        'bookings.actions.createBooking.outcomeUnknown',
        NotificationTypes.DANGER,
      );
      this.goToList.emit(this.bookingMultiple);
      return;
    }
    if (errorCode === ErrorsCode.ENTITY_ALREADY_EXISTS) {
      this.utilsService.showNotification(
        images.notifications.warning,
        `${errorCode}_ERROR.bookings`,
        NotificationTypes.DANGER,
      );
    }
    this.back();
  }

  makeBookingArray(trips: any) {
    const newBookings: IBookingCreate[] = [] as IBookingCreate[];
    const legs = trips.flatMap((trip: any) =>
      Array.isArray(trip) ? trip : [trip],
    );
    legs.forEach((availability: any) => {
      let booking = { ...this.newBooking };
      booking.availabilityId = availability.availabilityId;
      booking.originStopId = availability.inStop.id!;
      booking.originStop = {
        id: availability.inStop.id,
        name: availability.inStop.name,
        latitude: availability.inStop.latitude,
        longitude: availability.inStop.longitude,
      };
      // Cloning avoids mutating this.newBooking.destinationStops, which every booking in
      // this loop would otherwise share by reference, overwriting each other's exitStop.
      booking.destinationStops = this.newBooking.destinationStops.map(
        (destination: any) => ({
          ...destination,
          exitStop: {
            id: availability.outStop.id,
            name: availability.outStop.name,
            latitude: availability.outStop.latitude,
            longitude: availability.outStop.longitude,
          },
        }),
      );
      booking.destinationId = availability.outStop.id!;
      booking.serviceAvailabilityResponseId =
        availability.serviceAvailabilityResponseId;
      booking.serviceId = availability.serviceId;
      booking.tripId = availability.expeditionId;
      newBookings.push(booking);
    });
    return newBookings;
  }

  showMaxReservationTimeModal() {
    this.showMaxReservationTimeModalEventEmitter.emit();
    this.back();
  }

  showExceedingKmLimitModal(multiple: boolean) {
    this.bookingMultiple = multiple;
    this.showExceedingKmLimitModalEventEmitter.emit(this.tripsToBook(multiple));
  }

  showBlockedUserModal(penalty: any) {
    this.showBlockedUserModalEventEmitter.emit({
      penalty: penalty,
      booking: this.newBooking,
    });
  }

  getDate(): string {
    return moment(this.availabilitiesDates[this.currentPage]).format(
      'DD/MM/YYYY',
    );
  }

  async back() {
    const target = Math.max(1, this.currentStep - 1);
    this.isBack = true;
    this.availabilityDetail = undefined;
    if (target === 1) {
      // Favourite/recurrent flows came in from the user-details page; leaving
      // step 2 returns to that page rather than to an empty new-booking form.
      if (
        (this.bookingFromFavourite || this.favouriteByParams) &&
        this.activatedRoute.snapshot.queryParamMap.get('userId')
      ) {
        this.router.navigate(
          [
            '/users/' +
              this.activatedRoute.snapshot.queryParamMap.get('userId'),
          ],
          { state: { fromFavouriteBooking: true } },
        );
        return;
      }
      this.steps = [1];
      this.currentStep = 1;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
      });
      this.newBooking = {} as IBookingCreate;
      this.newBooking.originStop = undefined;
      this.newBooking.destinationStop = undefined;
      this.newBooking.destinationStops = [];
      this.newBooking.stops = [];
      this.newBooking.date = moment().format('YYYY-MM-DD');
      this.newBooking.time = moment().format('HH:mm');
      this.newBooking.door2DoorOriginFreeStops = true;
      this.newBooking.door2DoorDestinationFreeStops = true;
      this.possibleSwap = false;
      const loggedUserTown = await this.usersService.getLoggedUserTown();
      this.getServiceInfosEventEmitter.emit({
        serviceInfos: [],
        townId: loggedUserTown.id,
      });
      this.changeTown.emit();
    } else {
      this.goToStep(target);
      this.creatingBooking = false;
      this.availabilitiesDates = [];
      this.availabilitiesSeparateByDate = {};
      this.availabilitiesSelected = [];
      this.currentPage = 0;
    }
    await this.updateBookingForm(new UserBookingData({ showMoreUsers: false }));
  }

  async changeStops() {
    // The swap awaits network + form rebuilds; a second click landing mid-flight
    // would snapshot a half-swapped newBooking and corrupt origin/destination.
    if (this.swappingStops) return;
    this.swappingStops = true;
    try {
      await this.runChangeStops();
    } finally {
      this.swappingStops = false;
    }
  }

  private async runChangeStops() {
    const newBookingCopy = { ...this.newBooking };
    if (this.isD2DBooking) {
      this.stops = [];
      const originStop = { ...this.newBooking.originStop };
      const originFreeStop = this.newBooking.door2DoorOriginFreeStops || false;
      this.newBooking.originStop = this.newBooking.destinationStop;
      this.newBooking.door2DoorOriginFreeStops =
        this.newBooking.door2DoorDestinationFreeStops || false;
      this.selectODEventEmitter.emit({
        newBooking: this.newBooking,
        type: 'origin',
      });
      await this.updateBookingForm(undefined, ['originStopId']);

      this.newBooking.destinationStop = originStop;
      this.newBooking.door2DoorDestinationFreeStops = originFreeStop;
      // Assign the .map result back — the previous code discarded it, leaving
      // exitStop.id at the old destination's id even after the swap. Harmless
      // for free-point D2D (ids are undefined either way → position endpoint),
      // but with real-stop D2D rebook it produced in_stop_id == out_stop_id.
      this.newBooking.destinationStops = this.newBooking.destinationStops.map(
        (stop) => ({
          ...stop,
          exitStop: {
            id: this.newBooking.door2DoorDestinationFreeStops
              ? undefined
              : originStop.id,
            name: originStop.name,
          },
        }),
      );
      if (!this.newBooking.door2DoorDestinationFreeStops) {
        this.stops = await this.getStopsReachableFromOrigin();
        if (
          this.newBooking.destinationStop &&
          !this.stops.some(
            (stop: any) => stop.id === this.newBooking.destinationStop.id,
          )
        ) {
          this.newBooking.destinationStops = [];
          this.newBooking.destinationStop = null;
          this.selectODEventEmitter.emit({
            newBooking: this.newBooking,
            type: 'destination',
            isSingleReservation: false,
          });
        }
      }
      this.selectStop.emit({
        destinationsStops: this.stops,
        showMarkers: true,
      });
      this.selectODEventEmitter.emit({
        newBooking: this.newBooking,
        type: 'destination',
      });
      await this.updateBookingForm(undefined, ['destinationsStops', 'stop']);

      // [DISABLED] singleStopReservation feature - was: } else if (!this.newBooking.serviceInfo.singleStopReservations)
    } else if (true) {
      let oppositeStop = this.newBooking.destinationStops[0].exitStop.id!;
      const origin =
        this.newBooking.stops.find((stop: any) => stop.id === oppositeStop) ||
        this.newBooking.stops.find(
          (stop: any) => Math.abs(stop.id) === Math.abs(oppositeStop),
        );
      this.setLocation(origin.id, true);
      this.newBooking.originStopId = origin.id;
      this.selectODEventEmitter.emit({
        newBooking: this.newBooking,
        type: 'origin',
      });

      this.stops = this.isTripPlanner
        ? this.getAllServiceStops()
        : await this.townsService.getStopsFrom(
            this.newBooking.townId,
            this.newBooking.originStopId,
          );
      this.newBooking.destinationStops = [];
      await this.updateBookingForm(undefined, ['stop']);

      oppositeStop = newBookingCopy.originStopId;
      const destination: any =
        this.stops.find((stop: any) => stop.id === oppositeStop) ||
        this.stops.find(
          (stop: any) => Math.abs(stop.id) === Math.abs(oppositeStop),
        );
      destination &&
        newBookingCopy.destinationStops.forEach((destinationStop: any) => {
          destinationStop.exitStop.id = destination.id;
          destinationStop.exitStop.name = destination.name;
        });
      this.setLocation(destination.id, false);
      this.newBooking.destinationStops = !destination
        ? []
        : newBookingCopy.destinationStops;

      await this.updateBookingForm();
      this.selectODEventEmitter.emit({
        newBooking: this.newBooking,
        type: 'destination',
      });
      this.showMessageIfAnyStopAreIncompatible();
      // [DISABLED] singleStopReservation feature
      // } else {
      //   const singleStopReservationType = this.newBooking.destinationStops[0].exitStop.id !== 0 ? SingleStopReservationType.DESTINATION: SingleStopReservationType.ORIGIN;
      //   if (singleStopReservationType === SingleStopReservationType.DESTINATION) {
      //     this.newBooking.originStopId = newBookingCopy.destinationStops[0].exitStop.id!;
      //     this.newBooking.destinationStops.map((destinationStop: any) => {
      //       destinationStop.exitStop.id = 0;
      //       destinationStop.exitStop.name = '';
      //     });
      //   } else {
      //     this.newBooking.originStopId = 0;
      //     this.newBooking.destinationStops.map((destinationStop: any) => {
      //       destinationStop.exitStop.id = newBookingCopy.originStopId;
      //     });
      //   }
      //   await this.updateBookingForm();
    }
    this.syncFullState();
  }

  showMessageIfAnyStopAreIncompatible() {
    const { stops, originStopId, destinationStops } = this.newBooking;
    const stopOrigin =
      stops && stops.find((stop: any) => stop.id === originStopId);
    const stopDestination =
      destinationStops.length > 0 &&
      this.stops.find(
        (destinationStop: any) =>
          destinationStop.id === destinationStops[0].exitStop.id,
      );
    if (!stopOrigin && !stopDestination) {
      this.stopOriginNotification = true;
      this.possibleSwap = false;
      setTimeout(() => {
        this.stopOriginNotification = false;
      }, 5000);
      this.newBooking.originStopId = 0;
      this.newBooking.destinationStops = [];
    } else if (!stopOrigin) {
      this.stopOriginNotification = true;
      this.possibleSwap = false;
      setTimeout(() => {
        this.stopOriginNotification = false;
      }, 5000);
      this.newBooking.originStopId = 0;
    } else if (!stopDestination) {
      this.stopDestinationNotification = true;
      this.possibleSwap = false;
      setTimeout(() => {
        this.stopDestinationNotification = false;
      }, 5000);
      this.newBooking.destinationStops = [];
    }
  }

  disabledViewAvailabilities() {
    if (this.isD2DBooking) {
      return (
        !this.newBooking.date ||
        !this.newBooking.time ||
        !this.newBooking.originStop ||
        this.newBooking.destinationStops.length === 0
      );
    }
    // [DISABLED] singleStopReservation feature
    // return this.isSingleReservation ?
    //   !this.newBooking.originStopId && this.newBooking.destinationStops.length === 0 || !this.newBooking.date || !this.newBooking.time :
    return (
      !this.newBooking.originStopId ||
      this.newBooking.destinationStops.length === 0 ||
      !this.newBooking.date ||
      !this.newBooking.time
    );
  }

  previousAvailability() {
    this.currentPage > 0 && this.currentPage--;
  }

  previousAvailabilityDisabled() {
    return this.currentPage === 0;
  }

  nextAvailability(
    trip?:
      | IReservationAvailability
      | IReservationAvailabilityFailure
      | (IReservationAvailability | IReservationAvailabilityFailure)[],
  ) {
    this.currentPage <
      Object.keys(this.availabilitiesSeparateByDate).length - 1 &&
      this.currentPage++;
    if (trip) {
      const legs: (
        IReservationAvailability | IReservationAvailabilityFailure
      )[] = Array.isArray(trip) ? trip : [trip];
      const dateTime = moment(legs[0].dateTime).format('YYYY-MM-DD');
      const index = this.availabilitiesSelected.findIndex(
        (selectedTrip: any) => {
          const selectedLegs = Array.isArray(selectedTrip)
            ? selectedTrip
            : [selectedTrip];
          return (
            moment(selectedLegs[0].dateTime).format('YYYY-MM-DD') === dateTime
          );
        },
      );
      if (index !== -1) {
        this.availabilitiesSelected.splice(index, 1);
      }
      this.availabilitiesSelected.push(legs);
      if (this.showAvailabilityDetail(trip)) {
        this.availabilityDetail = legs;
      }
    }
  }

  showAvailabilityDetail(trip?: IAvailability | IAvailability[]) {
    return hasAvailabilityDetail(this.serviceInfos, trip);
  }

  nextAvailabilityDisabled() {
    const successTrips =
      this.availabilitiesSeparateByDate[
        this.availabilitiesDates[this.currentPage]
      ].success;
    const isAvailabilitySelected = this.availabilitiesSelected.some(
      (selectedTrip: any) => successTrips.includes(selectedTrip),
    );
    return (
      this.currentPage ===
        Object.keys(this.availabilitiesSeparateByDate).length - 1 ||
      (successTrips.length > 0 && !isAvailabilitySelected)
    );
  }

  checkAllAvailabilitiesSelected() {
    // A requested date is "covered" when either (a) its response has no success
    // options (only failures — nothing for the user to pick), or (b) the user
    // selected an availability that belongs to that date's success bucket.
    //
    // Matching by `serviceAvailabilityResponseId` rather than by
    // `moment(dateTime).format('YYYY-MM-DD')` is what makes this work when the
    // backend returns an alternative on a different calendar day than the one
    // requested (e.g. requested May 27, returned May 28). The previous date-
    // string compare always missed those, leaving the button stuck on
    // "automatic selection" even after the user picked the only available
    // option in a mixed success/failure response.
    return this.availabilitiesDates.every((date) => {
      const successesForDate =
        this.availabilitiesSeparateByDate[date]?.success || [];
      if (!successesForDate.length) return true;
      return this.availabilitiesSelected.some((selected: any) =>
        successesForDate.some(
          (trip: any) =>
            trip[0].serviceAvailabilityResponseId ===
            selected[0].serviceAvailabilityResponseId,
        ),
      );
    });
  }

  async onEditCustomStop(type: string) {
    if (type === 'origin') {
      this.newBooking.door2DoorOriginFreeStops =
        !this.newBooking.door2DoorOriginFreeStops;
    } else {
      this.newBooking.door2DoorDestinationFreeStops =
        !this.newBooking.door2DoorDestinationFreeStops;
    }
    this.onClearElement(type);
    this.possibleSwap = false;
  }

  async onClearElement(valueToChange: string) {
    switch (valueToChange) {
      case 'origin':
        this.stops = [];
        this.newBooking.originStop = undefined;
        this.newBooking.originStopId = 0;
        this.selectODEventEmitter.emit({
          newBooking: this.newBooking,
          type: 'origin',
        });
        await this.updateBookingForm(undefined, ['originStopId']);
        if (!this.newBooking.door2DoorOriginFreeStops) {
          const updatedStops = this.bookingForm.find(
            (element: any) => element.title === 'bookings.data.originStop',
          ).edit.values;
          this.selectStop.emit({
            destinationsStops: updatedStops,
            showMarkers: true,
          });
        } else {
          this.selectStop.emit({
            destinationsStops: bookableOriginStops(this.serviceInfos, this.adminRole),
            showMarkers: true,
          });
        }
        break;
      case 'destination':
        if (this.newBooking.originStop) {
          this.stops = await this.getStopsReachableFromOrigin();
          this.selectStop.emit({
            destinationsStops: this.stops,
            showMarkers: true,
          });
        } else {
          this.stops = [];
        }
        this.newBooking.destinationStop = undefined;
        this.newBooking.destinationStops = [];
        this.selectODEventEmitter.emit({
          newBooking: this.newBooking,
          type: 'destination',
        });
        await this.updateBookingForm(undefined, ['destinationsStops']);
        break;
    }
    this.possibleSwap = false;
    this.syncFullState();
  }

  disabledCreateFavourite() {
    return (
      this.newBooking.favouriteName === '' ||
      !this.newBooking.time ||
      (!this.newBooking.originStop && !this.newBooking.originStopId) ||
      this.newBooking.destinationStops.length === 0
    );
  }

  async createFavourite() {
    this.creatingNewFavourite = true;
    const favourite = {
      userId: this.newBooking.user.id,
      name: this.newBooking.favouriteName!,
      townId: this.newBooking.townId,
      time: this.newBooking.time,
      inStop: {
        id:
          this.isD2DBooking && this.newBooking.door2DoorOriginFreeStops
            ? APP.FAVOURITE_FREE_IN_STOP_ID
            : this.newBooking.originStopId,
        name: this.newBooking.originStop.name,
        latitude: this.newBooking.originStop.lat,
        longitude: this.newBooking.originStop.lng,
      },
      outStop: {
        id:
          this.isD2DBooking && this.newBooking.door2DoorDestinationFreeStops
            ? APP.FAVOURITE_FREE_OUT_STOP_ID
            : this.newBooking.destinationStops[0].exitStop.id,
        name: this.newBooking.destinationStop.name,
        latitude: this.newBooking.destinationStop.lat,
        longitude: this.newBooking.destinationStop.lng,
      },
      seats: this.newBooking.destinationStops,
    };
    await this.favouritesService.createFavourite(favourite).then(() => {
      this.utilsService.showNotification(
        images.notifications.check,
        'bookings.actions.createFavourite.success',
        NotificationTypes.SUCCESS,
      );
      this.router
        .navigate(['users', this.newBooking.user.id!], {
          state: {
            fromFavouriteBooking: true,
          },
        })
        .then(() => {
          this.creatingNewFavourite = false;
        });
    });
  }

  editFavouriteAction() {
    this.creatingNewFavourite = true;
    const favourite = {
      id: this.newBooking.favourite!.id,
      userId: this.newBooking.user.id,
      name: this.newBooking.favouriteName!,
      townId: this.newBooking.townId,
      time: this.newBooking.time,
      passengers: this.getPax(),
      prmPassengers: this.getPrm(),
      inStop: {
        id:
          this.isD2DBooking && this.newBooking.door2DoorOriginFreeStops
            ? APP.FAVOURITE_FREE_IN_STOP_ID
            : this.newBooking.originStopId,
        name: this.newBooking.originStop.name,
        latitude: this.newBooking.originStop.lat,
        longitude: this.newBooking.originStop.lng,
      },
      outStop: {
        id:
          this.isD2DBooking && this.newBooking.door2DoorDestinationFreeStops
            ? APP.FAVOURITE_FREE_OUT_STOP_ID
            : this.newBooking.destinationStops[0].exitStop.id,
        name: this.newBooking.destinationStop.name,
        latitude: this.newBooking.destinationStop.lat,
        longitude: this.newBooking.destinationStop.lng,
      },
      seats: this.newBooking.destinationStops,
    };
    this.favouritesService.editFavourite(favourite.id!, favourite).then(() => {
      this.utilsService.showNotification(
        images.notifications.check,
        'bookings.actions.editFavourite.success',
        NotificationTypes.SUCCESS,
      );
      this.router
        .navigate(['users', this.newBooking.user.id!], {
          state: {
            fromFavouriteBooking: true,
          },
        })
        .then(() => {
          this.creatingNewFavourite = false;
        });
    });
  }

  getPax() {
    return this.newBooking.destinationStops.filter((it) => !it.prm).length;
  }

  getPrm() {
    return this.newBooking.destinationStops.filter((it) => it.prm).length;
  }

  recurrentElements(element: any) {
    return (
      element.title === 'bookings.data.recurrentName' ||
      element.title === 'bookings.data.endDate' ||
      element.title === 'bookings.data.startDate' ||
      element.title === 'bookings.data.repeat' ||
      element.title === 'bookings.data.days'
    );
  }

  disabledCreateRecurrent() {
    return (
      this.newBooking.scheduler === undefined ||
      !this.newBooking.scheduler.name ||
      this.newBooking.scheduler.name === '' ||
      !this.newBooking.scheduler.endDate ||
      !this.newBooking.scheduler.startDate ||
      !this.newBooking.scheduler.repeatEvery ||
      !this.newBooking.scheduler.daysOfWeek ||
      !this.newBooking.time ||
      !this.newBooking.originStop ||
      this.newBooking.destinationStops.length === 0
    );
  }

  async createRecurrent() {
    this.creatingRecurrent = true;
    const scheduler: IBookingScheduler = {
      name: this.newBooking.scheduler.name,
      repeatEvery: this.newBooking.scheduler.repeatEvery,
      daysOfWeek: this.newBooking.scheduler.daysOfWeek?.map((dayOfWeek: any) =>
        dayOfWeek.id.toUpperCase(),
      ),
      bookingTime: this.newBooking.scheduler.bookingTime,
      favorite: { id: this.newBooking.favourite?.id },
      endDate: moment(this.newBooking.scheduler.endDate, 'DD/MM/YYYY').format(
        'YYYY-MM-DD',
      ),
      startDate: moment(
        this.newBooking.scheduler.startDate,
        'DD/MM/YYYY',
      ).format('YYYY-MM-DD'),
    };
    await this.bookingSchedulersService.createBookingScheduler(scheduler).then(
      () => {
        this.creatingRecurrent = false;
        this.utilsService.showNotification(
          images.notifications.check,
          'bookings.actions.createRecurrent.success',
          NotificationTypes.SUCCESS,
        );
        this.router.navigate(['users', this.newBooking.user.id!], {
          state: {
            fromFavouriteBooking: true,
          },
        });
      },
      (error) => {
        this.creatingRecurrent = false;
      },
    );
  }

  async editRecurrent() {
    this.editingRecurrent = true;
    const scheduler: IBookingScheduler = {
      ...{ id: this.newBooking.scheduler.id },
      name: this.newBooking.scheduler.name,
      repeatEvery: this.newBooking.scheduler.repeatEvery,
      daysOfWeek: this.newBooking.scheduler.daysOfWeek?.map((dayOfWeek: any) =>
        dayOfWeek.id ? dayOfWeek.id.toUpperCase() : dayOfWeek.toUpperCase(),
      ),
      bookingTime: this.newBooking.scheduler.bookingTime,
      favorite: {
        id:
          this.newBooking.favourite?.id ||
          this.newBooking.scheduler.favorite.id,
      },
      endDate: moment(
        this.newBooking.scheduler.endDate,
        this.newBooking.scheduler.endDate!.includes('-')
          ? 'YYYY-MM-DD'
          : 'DD/MM/YYYY',
      ).format('YYYY-MM-DD'),
      startDate: moment(
        this.newBooking.scheduler.startDate,
        this.newBooking.scheduler.startDate!.includes('-')
          ? 'YYYY-MM-DD'
          : 'DD/MM/YYYY',
      ).format('YYYY-MM-DD'),
    };
    await this.bookingSchedulersService.updateBookingScheduler(scheduler).then(
      () => {
        this.editingRecurrent = false;
        this.utilsService.showNotification(
          images.notifications.check,
          'bookings.actions.editRecurrent.success',
          NotificationTypes.SUCCESS,
        );
        this.router.navigate(['users', this.newBooking.user.id!], {
          state: {
            fromFavouriteBooking: true,
          },
        });
      },
      (error) => {
        this.editingRecurrent = false;
      },
    );
  }
}
